import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { NftPdpExhibitionNoteRichText } from './NftPdpExhibitionNoteRichText/NftPdpExhibitionNoteRichText.tsx';
import { NftPdpExhibitionNoteVideo } from './NftPdpExhibitionNoteVideo/NftPdpExhibitionNoteVideo.tsx';
import { NftPdpExhibitionNoteImageList } from './NftPdpExhibitionNoteImageList/NftPdpExhibitionNoteImageList.tsx';
import { type NftPdpExhibitionNotes_nftItem$key } from './__generated__/NftPdpExhibitionNotes_nftItem.graphql.ts';

import styles from './NftPdpExhibitionNotes.scss';

type Props = {
    nftItem: NftPdpExhibitionNotes_nftItem$key;
};

export const NftPdpExhibitionNotes: FC<Props> = ({ nftItem: nftItemRef }) => {
    const nftItem = useFragment(
        graphql`
            fragment NftPdpExhibitionNotes_nftItem on NftItem {
                exhibitionNotes {
                    contentList {
                        __typename
                        ... on NftExhibitionNoteImageListContent {
                            ...NftPdpExhibitionNoteImageList_note
                        }
                        ... on NftExhibitionNoteRichTextContent {
                            ...NftPdpExhibitionNoteRichText_note
                        }
                        ... on NftExhibitionNoteVideoEmbedContent {
                            ...NftPdpExhibitionNoteVideo_note
                        }
                    }
                }
            }
        `,
        nftItemRef
    );
    const { exhibitionNotes } = nftItem || {};
    if (!exhibitionNotes || exhibitionNotes?.contentList?.length === 0) {
        return null;
    }
    const body = exhibitionNotes?.contentList?.map((c, i) => {
        switch (c?.__typename) {
            case 'NftExhibitionNoteImageListContent':
                return <NftPdpExhibitionNoteImageList key={i} note={c} />;
            case 'NftExhibitionNoteRichTextContent':
                return <NftPdpExhibitionNoteRichText key={i} note={c} />;
            case 'NftExhibitionNoteVideoEmbedContent':
                return <NftPdpExhibitionNoteVideo key={i} note={c} />;
            default:
                return null;
        }
    });
    return (
        <div className={styles.wrapper}>
            <HeadingLevel>
                {Heading => (
                    <div className={styles.body}>
                        <Heading className={styles.header}>
                            <FormattedMessage
                                id="nft.pdp.exhibitionNotes.header"
                                defaultMessage="Additional Info"
                            />
                        </Heading>
                        {body}
                    </div>
                )}
            </HeadingLevel>
            <div className={styles.mobileSeparatorContainer}>
                <div className={styles.mobileSeparator} />
            </div>
        </div>
    );
};
